import { Button } from "@amzn/awsui-components-console";
import React from "react";

import { useAdminCachedMessageActions } from "@/hooks/admin/useAdminCachedMessageActions";

import { MessageItem, UpdateCacheAction } from "../cached-messages/types";
import { CACHE_ACTION_ICONS } from "./config";

export interface CacheButtonProps {
  message: MessageItem | undefined;
  /**
   * Called once the request settled, with the state the entry is now in.
   *
   * Only called for an entry the backend confirmed changed. The endpoint answers
   * `200` even when an entry failed, so acting on the request completing rather
   * than on its per-entry outcome is what previously let the UI show a success
   * toast for an entry that never changed.
   *
   * What this means is the caller's choice: the table edits that one row in place,
   * while the details page refetches the single entry it is showing.
   */
  onSettled: (id: string, cached: boolean) => void;
  loading?: boolean;
}

const CacheButton: React.FC<CacheButtonProps> = ({
  message,
  onSettled,
  loading: loadingProp,
}) => {
  const {
    loading: actionLoading,
    cacheMessages,
    uncacheMessages,
  } = useAdminCachedMessageActions();

  const isCached = message?.cached;

  const handleClick = async () => {
    if (!message) {
      return;
    }

    const nextCached = !isCached;
    const { settled } = nextCached
      ? await cacheMessages(message.id)
      : await uncacheMessages(message.id);

    if (settled.includes(message.id)) {
      onSettled(message.id, nextCached);
    }
  };

  const loading = loadingProp || actionLoading;

  return (
    <Button
      variant={isCached ? "normal" : "primary"}
      iconName={
        loading
          ? undefined
          : CACHE_ACTION_ICONS[
              isCached ? UpdateCacheAction.UNCACHE : UpdateCacheAction.CACHE
            ]
      }
      loading={loading}
      disabled={loading}
      ariaLabel={`${isCached ? "Uncache" : "Cache"} ${message?.chatName}`}
      onClick={handleClick}
    >
      {loadingProp ? "Loading..." : isCached ? "Uncache" : "Cache"}
    </Button>
  );
};

export default CacheButton;
